import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Mail, ArrowDownLeft, ArrowUpRight, Paperclip, Hand, Unplug, ShieldCheck, PenSquare, ExternalLink, Download, Unlink, History, Loader2, Reply,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Button, Empty, ErrorBlock, Skeleton, SearchInput, Sheet, errMsg, fmtDate, downloadFile } from '../SupportCoordination/kit';
import Body from './MessageBody';
import { avatarColor, initials, listTime, fullTime } from './mailUi';

// The Email tab on a participant's file (SC, PM or client): every email with or about them, kept
// for the company's retention period even after it is deleted from the mailbox. Found from the
// addresses on the file, or linked by hand in Email.
const PAGE = 50;
const PART = { sc: 'Support Coordination', pm: 'Plan Management', care: 'Clients' };
const addr = (xs) => (xs || []).map((a) => a.name || a.address).filter(Boolean).join(', ');
const other = (r) => (r.direction === 'out' ? (r.to || [])[0] : (r.from || [])[0]) || {};

export default function ParticipantEmail({ clientId, name, to = [], bare = false }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [more, setMore] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const load = useCallback(async (offset = 0) => {
    if (offset) setMore(true); else setError(null);
    try {
      const r = await api.get('/mail/records', { params: { client_id: clientId, q: term || undefined, limit: PAGE, offset } });
      setData((d) => (offset && d ? { ...r.data, rows: [...d.rows, ...r.data.rows] } : r.data));
    } catch (e) { if (offset) toast.error(errMsg(e)); else setError(errMsg(e, 'Email could not be loaded.')); }
    finally { setMore(false); }
  }, [clientId, term]);
  useEffect(() => { setData(null); load(0); }, [load]);

  const first = (to || []).find((x) => x && x.address);
  const compose = () => {
    const p = new URLSearchParams({ compose: '1' });
    if (first) { p.set('to', first.address); if (first.name) p.set('to_name', first.name); }
    navigate(`/mail?${p.toString()}`);
  };
  const years = data ? data.retention_years : 7;
  const rows = data ? data.rows : [];

  const Frame = bare ? BareFrame : Panel;
  return (
    <Frame icon={Mail} hue="blue" title="Email"
      sub={`Email with or about ${name || 'this participant'}. Kept for ${years} years from the day it was sent, even if it is deleted from the mailbox.`}
      actions={<Button size="sm" icon={PenSquare} onClick={compose}>New email</Button>}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <SearchInput value={q} onChange={setQ} placeholder="Search subject or address" className="sm:w-80" />
        <p className="text-[12px] text-slate-500 sm:ml-auto">
          {data ? `${data.total} email${data.total === 1 ? '' : 's'}` : ''}
        </p>
      </div>
      {error ? <ErrorBlock error={error} onRetry={() => load(0)} />
        : !data ? <ListSkeleton />
          : !rows.length ? (
            <Empty icon={Mail} title={term ? 'No email matches that' : 'No email on this file yet'}>
              {term ? 'Try another word or address.' : (
                <>Email from or to an address on this file (the participant, a nominee or a contact) lands here by itself.
                  For any other email, open it in Email and choose <b>Link to a participant</b>.</>
              )}
            </Empty>
          ) : (
            <>
              <ul className="divide-y divide-slate-100 border-y border-slate-100 -mx-1">
                {rows.map((r) => <Row key={r.id} r={r} onOpen={() => setOpenId(r.id)} />)}
              </ul>
              {rows.length < data.total && (
                <div className="pt-3 text-center">
                  <Button variant="ghost" size="sm" loading={more} onClick={() => load(rows.length)}>Show more</Button>
                </div>
              )}
            </>
          )}
      <p className="mt-3 flex items-start gap-1.5 text-[11.5px] text-slate-500">
        <ShieldCheck size={13} className="mt-px flex-shrink-0 text-emerald-600" aria-hidden />
        Kept encrypted. Who opens, downloads or removes an email is recorded. Mail from someone&apos;s private mailbox shows only to them.
      </p>
      <RecordSheet id={openId} clientId={clientId} name={name} onClose={() => setOpenId(null)}
        onRemoved={() => { setOpenId(null); load(0); }} />
    </Frame>
  );
}

function BareFrame({ icon: Icon, title, sub, actions, children }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Icon size={15} className="text-sky-600" aria-hidden />{title}</h3>
          {sub && <p className="text-[12px] text-slate-500 mt-0.5">{sub}</p>}
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-slate-100 border-y border-slate-100" aria-busy="true" aria-label="Loading email">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="flex gap-3 py-3">
          <Skeleton className="w-9 h-9 !rounded-full" />
          <div className="flex-1 space-y-2"><Skeleton className="h-3.5 w-1/3" /><Skeleton className="h-3 w-2/3" /></div>
        </li>
      ))}
    </ul>
  );
}

function Row({ r, onOpen }) {
  const o = other(r);
  const out = r.direction === 'out';
  const label = o.name || o.address || '(unknown)';
  return (
    <li>
      <button type="button" onClick={onOpen}
        className="group w-full text-left flex gap-3 px-1 py-3 rounded-lg hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400">
        <span className="relative flex-shrink-0">
          <span className="w-9 h-9 rounded-full text-white text-[12px] font-bold flex items-center justify-center" style={{ background: avatarColor(o.address || label) }} aria-hidden>
            {initials(o.name, o.address)}
          </span>
          <span className={clsx('absolute -right-1 -bottom-1 w-[18px] h-[18px] rounded-full ring-2 ring-white flex items-center justify-center', out ? 'bg-emerald-500' : 'bg-sky-500')}
            title={out ? 'Sent' : 'Received'}>
            {out ? <ArrowUpRight size={11} className="text-white" aria-hidden /> : <ArrowDownLeft size={11} className="text-white" aria-hidden />}
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="truncate text-[13.5px] font-semibold text-slate-900">{out ? `To ${label}` : label}</span>
            <span className="ml-auto flex-shrink-0 text-[12px] text-slate-500 tabular-nums">{listTime(r.date)}</span>
          </span>
          <span className="block truncate text-[13px] text-slate-800">{r.subject || '(no subject)'}</span>
          <span className="flex items-center gap-2 mt-0.5">
            <span className="truncate text-[12.5px] text-slate-500">{r.snippet}</span>
            <span className="ml-auto flex flex-shrink-0 items-center gap-1.5">
              {r.attachment_count > 0 && <span className="inline-flex items-center gap-0.5 text-[11.5px] text-slate-500" title={`${r.attachment_count} attachment${r.attachment_count === 1 ? '' : 's'}`}><Paperclip size={12} aria-hidden />{r.attachment_count}</span>}
              {r.how === 'hand' && <Chip icon={Hand} tone="violet" title="Linked to this file by hand">By hand</Chip>}
              {r.mailbox_gone && <Chip icon={Unplug} tone="slate" title="The mailbox it came from is disconnected. The kept copy stays.">Disconnected</Chip>}
            </span>
          </span>
        </span>
      </button>
    </li>
  );
}

function Chip({ icon: Icon, tone, title, children }) {
  return (
    <span title={title} className={clsx('inline-flex items-center gap-1 px-1.5 py-px rounded text-[10.5px] font-semibold',
      tone === 'violet' ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-600')}>
      <Icon size={10} aria-hidden />{children}
    </span>
  );
}

function RecordSheet({ id, clientId, name, onClose, onRemoved }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);
  const [images, setImages] = useState(false);
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(null);
  const admin = user && ['admin', 'super_admin'].includes(user.role);

  useEffect(() => { setM(null); setError(null); setImages(false); setLog(null); }, [id]);
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    api.get(`/mail/records/${id}`, { params: { client_id: clientId, images: images ? 1 : undefined } })
      .then((r) => { if (alive) setM(r.data); })
      .catch((e) => { if (alive) setError(errMsg(e, 'This email could not be opened.')); });
    return () => { alive = false; };
  }, [id, clientId, images]);

  const download = async () => {
    setBusy('eml');
    try { await downloadFile(`/mail/records/${id}/eml`, { params: { client_id: clientId } }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const remove = async () => {
    if (!await confirm(`Take this email off ${name || 'this participant'}'s file? It stays in the mailbox, and a sync will not put it back. The kept copy is still held until its date, on no file.`,
      { title: 'Remove from this file', danger: true, confirmLabel: 'Remove' })) return;
    setBusy('remove');
    try { await api.post(`/mail/records/${id}/unlink`, { client_id: clientId }); toast.success('Removed from this file'); onRemoved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const showLog = async () => {
    if (log) { setLog(null); return; }
    try { const r = await api.get(`/mail/records/${id}/access`); setLog(r.data || []); } catch (e) { toast.error(errMsg(e)); }
  };
  const inEmail = () => navigate(`/mail?open=${m.message_id}`);

  return (
    <Sheet open={!!id} onClose={onClose} width="lg" hue="blue" icon={Mail}
      title={m ? (m.subject || '(no subject)') : 'Email'}
      sub={m ? `${m.direction === 'out' ? 'Sent' : 'Received'} ${fullTime(m.date)}${m.account_email ? ` · ${m.account_email}` : ''}` : ''}
      footer={m && (
        <>
          {/* Admins, or whoever linked it by hand (the server checks it was them). */}
          {admin || m.how === 'hand'
            ? <Button variant="ghost" className="mr-auto text-rose-600 hover:bg-rose-50" icon={Unlink} loading={busy === 'remove'} onClick={remove}>Remove from this file</Button>
            : <span className="mr-auto" />}
          <Button variant="secondary" icon={Download} loading={busy === 'eml'} onClick={download}>Download</Button>
          {m.message_id
            ? <Button icon={Reply} onClick={inEmail}>Open in Email</Button>
            : <Button variant="secondary" icon={ExternalLink} disabled title="No longer in a mailbox you can open">Open in Email</Button>}
        </>
      )}>
      {error ? <ErrorBlock error={error} />
        : !m ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" aria-hidden />Opening…</div>
          : (
            <div className="space-y-4">
              <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50/60 px-4 py-3 space-y-1 text-[13px]">
                <Line label="From" value={addr(m.from)} />
                <Line label="To" value={addr(m.to)} />
                {(m.cc || []).length > 0 && <Line label="Cc" value={addr(m.cc)} />}
                {(m.links || []).some((l) => l.client_id !== clientId) && <Line label="Also on" value={m.links.filter((l) => l.client_id !== clientId).map((l) => `${l.label} (${PART[l.module] || PART.care})`).join(', ')} />}
              </div>
              {m.mailbox_gone && (
                <p className="flex items-start gap-2 rounded-lg bg-slate-100 px-3 py-2 text-[12.5px] text-slate-700">
                  <Unplug size={14} className="mt-0.5 flex-shrink-0" aria-hidden />The mailbox it came from has been disconnected. This kept copy stays on the file until {fmtDate(m.retain_until)}.
                </p>
              )}
              {m.blocked_images > 0 && !images && (
                <p className="flex items-center gap-2 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12.5px] text-amber-900">
                  Pictures from the internet are hidden to protect your privacy.
                  <button type="button" className="ml-auto font-semibold underline" onClick={() => setImages(true)}>Show pictures</button>
                </p>
              )}
              <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden bg-white">
                <Body html={m.html} text={m.text} confirmLinks />
              </div>
              {(m.attachments || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Attachments on the original</p>
                  <ul className="flex flex-wrap gap-1.5">
                    {m.attachments.map((a, i) => (
                      <li key={`${a.filename}-${i}`} className="inline-flex items-center gap-1 rounded-md ring-1 ring-slate-200 px-2 py-1 text-[12px] text-slate-700"><Paperclip size={12} aria-hidden />{a.filename || 'attachment'}</li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-[11.5px] text-slate-500">{m.message_id ? 'Open it in Email to save an attachment.' : 'The attachment files themselves were not kept.'}</p>
                </div>
              )}
              <p className="text-[11.5px] text-slate-500">
                Kept until {fmtDate(m.retain_until)}.{' '}
                {m.how === 'hand' ? 'Linked to this file by hand.' : m.role ? `On this file because of the ${m.role}'s address.` : ''}
              </p>
              {admin && (
                <div>
                  <button type="button" onClick={showLog} aria-expanded={!!log} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700 hover:text-slate-900">
                    <History size={14} aria-hidden />{log ? 'Hide who opened this' : 'Who opened this'}
                  </button>
                  {log && (
                    <ul className="mt-2 divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
                      {!log.length && <li className="px-3 py-2 text-[12.5px] text-slate-500">Nothing recorded yet.</li>}
                      {log.map((x, i) => (
                        <li key={i} className="flex items-center gap-2 px-3 py-2 text-[12.5px]">
                          <b className="text-slate-800">{x.who}</b><span className="text-slate-600">{x.label.toLowerCase()}</span>
                          <span className="ml-auto text-slate-500 tabular-nums">{fullTime(x.at)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
    </Sheet>
  );
}

function Line({ label, value }) {
  if (!value) return null;
  return <p className="flex gap-2"><span className="w-14 flex-shrink-0 text-slate-500">{label}</span><span className="min-w-0 break-words text-slate-800">{value}</span></p>;
}
